import { ClerkProvider, SignIn, SignUp, UserButton, useUser, SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import CloneScore from './CloneScore';
import AINews from './AINews';
import ZoomTab from './ZoomTab';
import InboxTab from './InboxTab';
import PeopleTab from './PeopleTab';

const PUBLISHABLE_KEY = "pk_live_Y2xlcmsuYWx0ZXJ1cy5pbyQ";
const API = 'https://alterus.onrender.com';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@600;700&family=Inter:wght@400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #0a0d16; color: #e8eaed; font-family: 'Inter', sans-serif; }
  .card { background: #10131e; border: 1px solid #1e2235; border-radius: 12px; padding: 16px; margin-bottom: 14px; }
  .card-accent { border-left: 3px solid #6366f1 !important; }
  .badge { background: #1e2235; color: #6366f1; font-size: 10px; padding: 2px 8px; border-radius: 10px; font-family: 'DM Mono', monospace; }
  .mono { font-family: 'DM Mono', monospace; }
  .tab-btn { padding: 8px 18px; border-radius: 8px; border: none; cursor: pointer; font-size: 13px; font-weight: 500; transition: all 0.2s; }
  .tab-btn.active { background: #6366f1; color: white; }
  .tab-btn.inactive { background: transparent; color: #5a607a; }
  .tab-btn.inactive:hover { color: #e8eaed; }
  .msg-item { background: #10131e; border: 1px solid #1e2235; border-radius: 8px; padding: 12px; margin-bottom: 8px; }
  .msg-item:hover { border-color: #6366f1; }
  .btn { padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer; font-size: 12px; font-weight: 500; }
  .btn-primary { background: #6366f1; color: white; }
  .btn-primary:hover { background: #5254cc; }
  .btn-secondary { background: #1e2235; color: #8b8fa8; }
  .btn-sm { padding: 5px 12px; font-size: 11px; }
  .input { width: 100%; background: #10131e; border: 1px solid #1e2235; border-radius: 8px; padding: 10px 12px; color: #e8eaed; font-size: 13px; outline: none; font-family: inherit; margin-bottom: 8px; }
  .input:focus { border-color: #6366f1; }
  .textarea { width: 100%; background: #10131e; border: 1px solid #1e2235; border-radius: 8px; padding: 10px 12px; color: #e8eaed; font-size: 12px; outline: none; font-family: 'DM Mono', monospace; resize: vertical; margin-bottom: 8px; }
  .textarea:focus { border-color: #6366f1; }
  .section-title { font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 700; color: #e8eaed; }
  .metric-card { background: #10131e; border: 1px solid #1e2235; border-radius: 8px; padding: 12px 16px; text-align: center; }
  .feed-item { padding: 8px 0; border-bottom: 1px solid #1e2235; }
  .feed-item:last-child { border-bottom: none; }
  .log-row { display: flex; gap: 12px; background: #10131e; border: 1px solid #1e2235; border-radius: 8px; padding: 10px 14px; margin-bottom: 6px; }
  .spinner { display: inline-block; width: 12px; height: 12px; border: 2px solid #1e2235; border-top: 2px solid #6366f1; border-radius: 50%; animation: spin 0.8s linear infinite; vertical-align: middle; margin-right: 6px; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .progress-bar { background: #1e2235; border-radius: 4px; height: 6px; overflow: hidden; }
  .progress-fill { height: 100%; border-radius: 4px; transition: width 1s ease; }
  .sub-tab { padding: 6px 14px; border-radius: 6px; border: 1px solid #1e2235; cursor: pointer; font-size: 12px; background: transparent; color: #8b8fa8; }
  .sub-tab.active { background: #1e2235; color: #6366f1; border-color: #6366f1; }
  pre { white-space: pre-wrap; word-break: break-word; }
`;

function Spinner() { return <span className="spinner" />; }

function SectionHeader({ icon, title, badge, badgeColor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span className="section-title">{title}</span>
      {badge && <span className="badge" style={badgeColor ? { background: badgeColor + '22', color: badgeColor } : {}}>{badge}</span>}
    </div>
  );
}

async function callAPI(endpoint, body) {
  const res = await fetch(`${API}${endpoint}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
  });
  return res.json();
}

// ── DASHBOARD TAB ─────────────────────────────────────────────────────────────
function DashboardTab({ email, name }) {
  const [briefing, setBriefing] = useState('');
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [agenda, setAgenda] = useState(localStorage.getItem('agenda') || '');

  const genBriefing = async () => {
    setBriefingLoading(true);
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    try {
      const res = await fetch('https://alterus.onrender.com/api/briefing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_name: name,
          user_email: email,
          agenda: agenda || '',
          date: today,
        }),
      });
      const data = await res.json();
      setBriefing(data.briefing || data.error || '');
    } catch(e) {
      setBriefing('Could not generate briefing. Check connection.');
    }
    setBriefingLoading(false);
  };

  const [workstreams, setWorkstreams] = useState(() => {
    const saved = localStorage.getItem('alterus_workstreams');
    return saved ? JSON.parse(saved) : [];
  });
  const [newWS, setNewWS] = useState({ name: '', status: '', stakeholder: '' });

  const addWorkstream = () => {
    if (!newWS.name) return;
    const updated = [...workstreams, { ...newWS, owner: name, tag: 'active' }];
    setWorkstreams(updated);
    localStorage.setItem('alterus_workstreams', JSON.stringify(updated));
    setNewWS({ name: '', status: '', stakeholder: '' });
  };

  const removeWorkstream = (i) => {
    const updated = workstreams.filter((_, idx) => idx !== i);
    setWorkstreams(updated);
    localStorage.setItem('alterus_workstreams', JSON.stringify(updated));
  };

  return (
    <div>
      <div style={{ background: '#10131e', border: '1px solid #6366f133', borderLeft: '3px solid #6366f1', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 12, color: '#8b8fa8' }}>
        🚀 <strong style={{ color: '#e8eaed' }}>Get started:</strong> Install extension → popup → enter <span style={{ color: '#6366f1', fontFamily: 'DM Mono' }}>{name}</span> / <span style={{ color: '#6366f1', fontFamily: 'DM Mono' }}>{email}</span> → open Gmail/Outlook/Teams → click ✦ → Draft Reply
      </div>

      {/* Row 1: Briefing + Clone Score */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 16, marginBottom: 20 }}>
        <div className="card card-accent">
          <SectionHeader icon="☀️" title="Daily Briefing" badge="AI · first person" />
          {briefingLoading
            ? <div style={{ color: '#5a607a', fontSize: 13 }}><Spinner />Writing your morning briefing...</div>
            : briefing
              ? <div style={{ fontSize: 13, lineHeight: 1.8, color: '#c8cce0', marginBottom: 12 }}>
                  {briefing.split('\n').map((line, i) => {
                    const clean = line.replace(/\*\*/g, '');
                    if (clean.startsWith('•') || clean.startsWith('-')) {
                      return <div key={i} style={{ paddingLeft: 12, marginBottom: 4 }}>{clean}</div>;
                    }
                    if (clean.endsWith(':') || clean.includes('Priorities:') || clean.includes('Response:') || clean.includes('Risk') || clean.includes('Mindset')) {
                      return <div key={i} style={{ fontWeight: 600, color: '#e8eaed', marginTop: 10, marginBottom: 4 }}>{clean}</div>;
                    }
                    return clean ? <div key={i} style={{ marginBottom: 4 }}>{clean}</div> : null;
                  })}
                </div>
              : <div style={{ color: '#3a3f55', fontSize: 12, fontFamily: 'DM Mono', marginBottom: 12 }}>Paste your agenda below and click Generate</div>
          }
          <button className="btn btn-primary btn-sm" onClick={genBriefing} disabled={briefingLoading}>
            {briefingLoading ? 'Generating...' : briefing ? '🔄 Regenerate' : '✨ Generate Briefing'}
          </button>
        </div>
        <div className="card">
          <SectionHeader icon="🎯" title="Clone Score" />
          <CloneScore email={email} dark={true} />
        </div>
      </div>

      {/* Row 2: Calendar */}
      <div className="card" style={{ marginBottom: 20 }}>
        <SectionHeader icon="📅" title="Today's Calendar" badge={new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} />
        <textarea className="textarea" value={agenda}
          onChange={e => { setAgenda(e.target.value); localStorage.setItem('agenda', e.target.value); }}
          placeholder={"9:00am — Standup with team\n11:00am — 1:1 with Jason Wong\n2:00pm — Product review"}
          style={{ minHeight: 80 }} />
        <div style={{ fontSize: 11, color: '#3a3f55', fontFamily: 'DM Mono' }}>Auto-saved · Used for briefing context</div>
      </div>

      {/* Row 3: Workstreams */}
      <div style={{ marginBottom: 20 }}>
        <SectionHeader icon="⚡" title="My Workstreams" badge={`${workstreams.length} active`} />
        {workstreams.length === 0 ? (
          <div style={{ background: '#10131e', border: '1px dashed #1e2235', borderRadius: 8, padding: '20px', textAlign: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>⚡</div>
            <div style={{ fontSize: 12, color: '#3a3f55', fontFamily: 'DM Mono', marginBottom: 12 }}>No workstreams yet — add your active projects below</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
            {workstreams.map((ws, i) => (
              <div key={i} style={{ background: '#10131e', border: '1px solid #1e2235', borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#e8eaed' }}>{ws.name}</span>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 8, background: ws.tag === 'active' ? '#22c55e22' : '#ef444422', color: ws.tag === 'active' ? '#22c55e' : '#ef4444', fontFamily: 'DM Mono' }}>{ws.tag}</span>
                    <button onClick={() => removeWorkstream(i)} style={{ background: 'none', border: 'none', color: '#3a3f55', cursor: 'pointer', fontSize: 10, padding: '0 2px' }}>✕</button>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#5a607a', marginBottom: 4 }}>{ws.status}</div>
                <div style={{ fontSize: 10, color: '#3a3f55', fontFamily: 'DM Mono' }}>👤 {ws.owner} · 📌 {ws.stakeholder}</div>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="input" placeholder="Project name" value={newWS.name} onChange={e => setNewWS({...newWS, name: e.target.value})} style={{ marginBottom: 0, flex: 1 }} />
          <input className="input" placeholder="Status" value={newWS.status} onChange={e => setNewWS({...newWS, status: e.target.value})} style={{ marginBottom: 0, flex: 1 }} />
          <input className="input" placeholder="Stakeholder" value={newWS.stakeholder} onChange={e => setNewWS({...newWS, stakeholder: e.target.value})} style={{ marginBottom: 0, flex: 1 }} />
          <button className="btn btn-primary" onClick={addWorkstream} style={{ whiteSpace: 'nowrap' }}>+ Add</button>
        </div>
      </div>

      {/* Row 4: News + Videos */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <SectionHeader icon="📰" title="AI News" badge="live" badgeColor="#22c55e" />
          <AINews dark={true} />
        </div>
        <div className="card">
          <SectionHeader icon="▶️" title="AI on YouTube" badge="top picks" />
          {[
            { title: 'Anthropic Claude 4 — What changed', channel: 'Anthropic', url: 'https://youtube.com/results?search_query=claude+ai' },
            { title: 'LangGraph multi-agent patterns 2025', channel: 'LangChain', url: 'https://youtube.com/results?search_query=langgraph+multi+agent' },
            { title: 'Building with MCP — full tutorial', channel: 'Fireship', url: 'https://youtube.com/results?search_query=MCP+claude' },
            { title: 'GPT-5 vs Claude 4 — benchmark deep dive', channel: 'AI Explained', url: 'https://youtube.com/results?search_query=gpt5+claude4+benchmark' },
            { title: 'Enterprise AI platform architecture', channel: 'AWS Events', url: 'https://youtube.com/results?search_query=enterprise+ai+platform' },
          ].map((v, i) => (
            <div key={i} className="feed-item">
              <div style={{ fontSize: 12, color: '#dde1f0', cursor: 'pointer' }} onClick={() => window.open(v.url, '_blank')}>{v.title}</div>
              <div style={{ fontSize: 10, color: '#ef4444', marginTop: 2, fontFamily: 'DM Mono' }}>▶ {v.channel}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── CREATE TAB ────────────────────────────────────────────────────────────────
function CreateTab({ name, email }) {
  const [prdTopic, setPrdTopic] = useState('');
  const [prdProblem, setPrdProblem] = useState('');
  const [prdResult, setPrdResult] = useState('');
  const [prdLoading, setPrdLoading] = useState(false);
  const [mirrorInput, setMirrorInput] = useState('');
  const [mirrorType, setMirrorType] = useState('Email to VP');
  const [mirrorResult, setMirrorResult] = useState('');
  const [mirrorLoading, setMirrorLoading] = useState(false);
  const [meetTitle, setMeetTitle] = useState('');
  const [meetAtt, setMeetAtt] = useState('');
  const [meetGoal, setMeetGoal] = useState('');
  const [meetDur, setMeetDur] = useState('30 min');
  const [meetResult, setMeetResult] = useState('');
  const [meetLoading, setMeetLoading] = useState(false);

  const genPRD = async () => {
    if (!prdTopic) return;
    setPrdLoading(true);
    const data = await callAPI('/api/generate-prd', { prompt: `Write a full PRD for: ${prdTopic}. Problem: ${prdProblem}`, user_name: name, user_email: email });
    setPrdResult(data.prd || '');
    setPrdLoading(false);
  };

  const genMirror = async () => {
    if (!mirrorInput) return;
    setMirrorLoading(true);
    const data = await callAPI('/api/generate-prd', { prompt: `Rewrite the following as a ${mirrorType} in ${name}'s voice, direct and analytical tone:\n\n${mirrorInput}`, user_name: name, user_email: email });
    setMirrorResult(data.prd || '');
    setMirrorLoading(false);
  };

  const genMeet = async () => {
    if (!meetTitle) return;
    setMeetLoading(true);
    const data = await callAPI('/api/generate-prd', { prompt: `Meeting prep for ${name}. Meeting: ${meetTitle}. Attendees: ${meetAtt}. Duration: ${meetDur}. Goal: ${meetGoal}. Include: 1) Opening line, 2) 3 talking points, 3) Likely pushback + response, 4) Decision needed. Under 180 words.`, user_name: name, user_email: email });
    setMeetResult(data.prd || '');
    setMeetLoading(false);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
      <div className="card card-accent">
        <SectionHeader icon="📄" title="PRD Generator" badge="your format" />
        <input className="input" placeholder="Feature / Product" value={prdTopic} onChange={e => setPrdTopic(e.target.value)} />
        <textarea className="textarea" placeholder="Problem statement..." value={prdProblem} onChange={e => setPrdProblem(e.target.value)} style={{ minHeight: 70 }} />
        <select className="input"><option>Internal (EAI/EDP)</option><option>CCX</option><option>GTM</option><option>External</option></select>
        <button className="btn btn-primary" style={{ width: '100%', marginBottom: 12 }} onClick={genPRD} disabled={prdLoading || !prdTopic}>
          {prdLoading ? <><Spinner />Generating...</> : '🚀 Generate PRD'}
        </button>
        {prdResult && <>
          <textarea className="textarea" value={prdResult} onChange={e => setPrdResult(e.target.value)} style={{ minHeight: 320 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={() => navigator.clipboard.writeText(prdResult)}>⎘ Copy</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setPrdResult(''); genPRD(); }}>🔄 Regen</button>
          </div>
        </>}
      </div>

      <div className="card">
        <SectionHeader icon="🪞" title="Style Mirror" badge="rewrite as you" />
        <textarea className="textarea" placeholder="Paste any draft to rewrite in your voice..." value={mirrorInput} onChange={e => setMirrorInput(e.target.value)} style={{ minHeight: 120 }} />
        <select className="input" value={mirrorType} onChange={e => setMirrorType(e.target.value)}>
          {['Email to VP', 'Email to peer', 'Teams message', 'PRD section', 'Strategy doc'].map(t => <option key={t}>{t}</option>)}
        </select>
        <button className="btn btn-primary" style={{ width: '100%', marginBottom: 12 }} onClick={genMirror} disabled={mirrorLoading || !mirrorInput}>
          {mirrorLoading ? <><Spinner />Mirroring...</> : '🪞 Mirror'}
        </button>
        {mirrorResult && <>
          <textarea className="textarea" value={mirrorResult} onChange={e => setMirrorResult(e.target.value)} style={{ minHeight: 260 }} />
          <button className="btn btn-primary btn-sm" onClick={() => navigator.clipboard.writeText(mirrorResult)}>⎘ Copy</button>
        </>}
      </div>

      <div className="card" style={{ borderColor: '#22c55e44' }}>
        <SectionHeader icon="🗓️" title="Meeting Prep" badge="your voice" badgeColor="#22c55e" />
        <input className="input" placeholder="Meeting title" value={meetTitle} onChange={e => setMeetTitle(e.target.value)} />
        <input className="input" placeholder="Attendees" value={meetAtt} onChange={e => setMeetAtt(e.target.value)} />
        <select className="input" value={meetDur} onChange={e => setMeetDur(e.target.value)}>
          <option>30 min</option><option>60 min</option><option>90 min</option>
        </select>
        <textarea className="textarea" placeholder="Your goal for this meeting..." value={meetGoal} onChange={e => setMeetGoal(e.target.value)} style={{ minHeight: 60 }} />
        <button className="btn btn-primary" style={{ width: '100%', marginBottom: 12 }} onClick={genMeet} disabled={meetLoading || !meetTitle}>
          {meetLoading ? <><Spinner />Preparing...</> : '🗓️ Generate Prep'}
        </button>
        {meetResult && <>
          <textarea className="textarea" value={meetResult} onChange={e => setMeetResult(e.target.value)} style={{ minHeight: 260 }} />
          <button className="btn btn-primary btn-sm" onClick={() => navigator.clipboard.writeText(meetResult)}>⎘ Copy</button>
        </>}
      </div>
    </div>
  );
}

// ── LOG TAB ───────────────────────────────────────────────────────────────────
function LogTab({ email }) {
  const [healerLoading, setHealerLoading] = useState(false);
  const [healerResult, setHealerResult] = useState(null);

  const mockLog = [];

  const actionColor = { approved: '#22c55e', rejected: '#ef4444', edited: '#f59e0b' };
  const scoreColor = s => s >= 0.8 ? '#22c55e' : s >= 0.65 ? '#f59e0b' : '#5a607a';

  const runHealer = async () => {
    setHealerLoading(true);
    try {
      const res = await fetch('https://alterus.onrender.com/api/healer/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_apply: false }),
      });
      const data = await res.json();
      if (data.analysis) {
        setHealerResult(data.analysis);
      } else if (data.error) {
        setHealerResult({ overall_assessment: data.error, top_issues: [], prompt_fixes: [] });
      }
    } catch(e) {
      setHealerResult({ overall_assessment: 'Failed to connect to healer', top_issues: [], prompt_fixes: [] });
    }
    setHealerLoading(false);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <SectionHeader icon="📋" title="Decision Log" badge="every draft outcome" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
          {[{ l: 'Total', v: mockLog.length, c: '#6366f1' }, { l: '✅ Approved', v: 2, c: '#22c55e' }, { l: '✏️ Edited', v: 1, c: '#f59e0b' }, { l: 'Avg Score', v: '85%', c: '#6366f1' }].map((m, i) => (
            <div key={i} className="metric-card">
              <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'Syne', color: m.c }}>{m.v}</div>
              <div style={{ fontSize: 10, color: '#5a607a', fontFamily: 'DM Mono', marginTop: 4 }}>{m.l}</div>
            </div>
          ))}
        </div>
        {mockLog.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px 0', color: '#3a3f55' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            <div style={{ fontFamily: 'DM Mono', fontSize: 12, lineHeight: 1.8 }}>
              No drafts logged yet.<br/>
              Use the Chrome extension to draft emails —<br/>
              every approval appears here automatically.
            </div>
          </div>
        ) : mockLog.map((entry, i) => (
          <div key={i} className="log-row">
            <div style={{ minWidth: 90 }}>
              <div style={{ color: actionColor[entry.action], fontSize: 10, fontFamily: 'DM Mono', fontWeight: 600 }}>{entry.action.toUpperCase()}</div>
              <div style={{ fontSize: 10, color: '#3a3f55', fontFamily: 'DM Mono', marginTop: 2 }}>{entry.timestamp}</div>
              <div style={{ fontSize: 9, color: '#6366f1', fontFamily: 'DM Mono', marginTop: 2 }}>{entry.tone}</div>
            </div>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 10, background: '#1e2235', padding: '1px 7px', borderRadius: 4, color: '#8b8fa8', fontFamily: 'DM Mono' }}>{entry.task_type}</span>
              <div style={{ fontSize: 12, color: '#8b8fa8', marginTop: 4 }}>📥 {entry.input}</div>
              <div style={{ fontSize: 11, color: '#5a607a', marginTop: 3 }}>📝 {entry.draft.slice(0, 60)}...</div>
            </div>
            <div style={{ minWidth: 45, textAlign: 'right', fontFamily: 'DM Mono', fontSize: 14, fontWeight: 700, color: scoreColor(entry.score) }}>{Math.round(entry.score * 100)}%</div>
          </div>
        ))}
      </div>

      <div className="card">
        <SectionHeader icon="🔄" title="Self-Healing Layer" badge="LangSmith powered" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
          {[{ l: 'Total Feedback', v: 0, c: '#6366f1' }, { l: '👍 Thumbs Up', v: 0, c: '#22c55e' }, { l: '👎 Thumbs Down', v: 0, c: '#ef4444' }, { l: '✏️ Edits', v: 0, c: '#f59e0b' }].map((m, i) => (
            <div key={i} className="metric-card">
              <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'Syne', color: m.c }}>{m.v}</div>
              <div style={{ fontSize: 10, color: '#5a607a', fontFamily: 'DM Mono', marginTop: 4 }}>{m.l}</div>
            </div>
          ))}
        </div>
        <div className="progress-bar" style={{ marginBottom: 6 }}>
          <div className="progress-fill" style={{ width: '0%', background: '#22c55e' }} />
        </div>
        <div style={{ fontSize: 11, color: '#5a607a', fontFamily: 'DM Mono', marginBottom: 16 }}>Approval rate: 0% — use extension and give feedback to improve</div>
        <button className="btn btn-primary" onClick={runHealer} disabled={healerLoading} style={{ marginBottom: 16 }}>
          {healerLoading ? <><Spinner />Analyzing patterns...</> : '🔄 Run Batch Healer'}
        </button>
        {healerResult && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#e8eaed', marginBottom: 8 }}>🔍 Issues found:</div>
            {healerResult.top_issues.map((issue, i) => (
              <div key={i} style={{ fontSize: 12, color: '#8b8fa8', marginBottom: 4 }}>· {issue}</div>
            ))}
            <div style={{ fontSize: 12, fontWeight: 600, color: '#e8eaed', margin: '12px 0 8px' }}>💡 Suggested improvements:</div>
            {healerResult.prompt_fixes.map((fix, i) => (
              <div key={i} style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 8, padding: '10px 14px', marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6366f1' }}>{fix.area}</div>
                <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>Problem: {fix.current_problem}</div>
                <div style={{ fontSize: 11, color: '#22c55e', marginTop: 4 }}>Fix: {fix.suggested_fix}</div>
              </div>
            ))}
            <div style={{ background: '#6366f111', border: '1px solid #6366f133', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#c8cce0', marginTop: 8 }}>
              💬 {healerResult.overall_assessment}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
function Dashboard() {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('dashboard');
  const email = user?.primaryEmailAddress?.emailAddress || '';
  const name = user?.fullName || user?.firstName || '';

  // Generate token and connect extension on login
  useEffect(() => {
    if (!email || !name) return;

    // Generate deterministic token from email
    const token = btoa(`alterus:${email}:${Date.now()}`).slice(0, 32);
    localStorage.setItem('alterus_token', token);

    // Send to extension via bridge
    window.postMessage({
      type: 'ALTERUS_CONNECT',
      name,
      email,
      userId: user?.id || email,
      token,
    }, '*');
  }, [email, name, user?.id]);

  const tabs = [
    { id: 'dashboard', label: '🏠 Dashboard' },
    { id: 'inbox', label: '📧 Inbox' },
    { id: 'create', label: '✍️ Create' },
    { id: 'log', label: '📋 Log' },
    { id: 'zoom', label: '🎥 Zoom' },
  { id: 'people', label: '👥 People' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0a0d16' }}>
      <style>{css}</style>
      <div style={{ background: '#10131e', borderBottom: '1px solid #1e2235', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 700, color: '#e8eaed' }}>✦ Alterus</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {tabs.map(tab => (
            <button key={tab.id} className={`tab-btn ${activeTab === tab.id ? 'active' : 'inactive'}`} onClick={() => setActiveTab(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>
        <UserButton afterSignOutUrl="/sign-in" />
      </div>
      <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
        {activeTab === 'dashboard' && <DashboardTab email={email} name={name} />}
        {activeTab === 'inbox' && <InboxTab name={name} email={email} />}
        {activeTab === 'create' && <CreateTab name={name} email={email} />}
        {activeTab === 'log' && <LogTab email={email} />}
        {activeTab === 'zoom' && <ZoomTab name={name} email={email} />}
        {activeTab === 'people' && <PeopleTab email={email} name={name} />}
      </div>
      <div style={{ textAlign: 'center', color: '#1e2235', fontSize: 10, fontFamily: 'DM Mono', padding: '20px 0', borderTop: '1px solid #1e2235' }}>
        Alterus v3.2 · Dashboard · Inbox · Create · Log · Zoom Intelligence
      </div>
    </div>
  );
}

function ProtectedDashboard() {
  return (
    <>
      <SignedIn><Dashboard /></SignedIn>
      <SignedOut><RedirectToSignIn /></SignedOut>
    </>
  );
}

function App() {
  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="/sign-in/*" element={<div style={{ display: 'flex', justifyContent: 'center', marginTop: 80, background: '#0a0d16', minHeight: '100vh' }}><SignIn routing="path" path="/sign-in" afterSignInUrl="/dashboard" /></div>} />
          <Route path="/sign-up/*" element={<div style={{ display: 'flex', justifyContent: 'center', marginTop: 80, background: '#0a0d16', minHeight: '100vh' }}><SignUp routing="path" path="/sign-up" afterSignUpUrl="/dashboard" /></div>} />
          <Route path="/dashboard" element={<ProtectedDashboard />} />
        </Routes>
      </BrowserRouter>
    </ClerkProvider>
  );
}

export default App;
